// SEQ-240 on SEQ-199: "How is this worked out" text for the grid widget (the
// Profit Pareto table and any rows-by-months grid).
//
// Built FROM the grid's own vocabulary, so the words cannot drift from the
// table: measure names come from GRID_MEASURES, summary column names from
// GRID_SUMMARY_COLUMNS (both src/lib/pnl/pnlGridConfig.ts), and each measure's
// meaning and formula from describeMetric for the registry metric it reads.
// gridDescriptions.test.ts fails when a measure or summary column has no text.
//
// WHAT IT DESCRIBES (merge of origin/main SEQ-199 into this branch):
// - gridModel.ts 176-217 (columns), 242-403 (month cells from project_month
//   rows with raw wages; Total from the project grain with multiplied wages;
//   the canonical highest-first order of the summary measure's Total for the
//   derived columns; Top N with an Other row summing dollar figures only; the
//   averages row as a simple mean of the project rows; bands off for money in
//   Safe Mode; the notes).
// - transforms/basic.ts (percent of total, cumulative percent, competition
//   rank), transforms/pareto.ts (a row is placed by the share held by the rows
//   ABOVE it), transforms/topN.ts, transforms/bands.ts (describeBands).
// - PnlGridWidget.tsx (Show months without costs, at most 24 months, Full
//   screen, exports off in Safe Mode).

import type { MetricDefinition } from "../../components/howWorkedOut/metricDefinition";
import { DEFAULT_PNL_METRIC_REGISTRY, PNL_METRIC_KEYS } from "../../lib/pnl/pnlAnalytics";
import {
  GRID_SUMMARY_COLUMNS,
  gridMeasure,
  resolveGridConfig,
  type GridSummaryColumn,
  type ResolvedGridConfig,
} from "../../lib/pnl/pnlGridConfig";
import type { PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import type { GridColumn } from "./gridModel";
import { describeMetric, metricUsesPayroll } from "./pnlMetricDescriptions";
import { payrollFreshness, widgetScope, type WidgetDescribeOptions } from "./pnlWidgetDescriptions";
import { describeBands, type FormatBand } from "./transforms/bands";
import { normaliseParetoConfig } from "./transforms/pareto";

/** The registry metric behind each grid measure (a PnlRollup field), so its words come from describeMetric. */
export const GRID_MEASURE_METRIC: Record<string, string> = {
  revenue: PNL_METRIC_KEYS.calculated.totalIncome,
  cost: PNL_METRIC_KEYS.calculated.totalCost,
  profit: PNL_METRIC_KEYS.calculated.netProfit,
  profitPercent: PNL_METRIC_KEYS.calculated.profitPercentage,
  patching: PNL_METRIC_KEYS.expense.patching,
  patchingPctRevenue: PNL_METRIC_KEYS.calculated.patchingPctRevenue,
  overtime: PNL_METRIC_KEYS.labour.overtime,
  overtimePctRevenue: PNL_METRIC_KEYS.calculated.overtimePctRevenue,
};

/** The measures whose month cells carry raw wages while the Total multiplies them (gridModel.ts, step 9). */
const RAW_WAGES_MEASURES: ReadonlySet<string> = new Set(["cost", "profit", "profitPercent"]);

const SOURCE: MetricDefinition["source"] = {
  kind: "mixed",
  detail:
    "Income from the progress claims, costs typed in on each project's P&L tab, overtime from payroll (KeyPay). Xero is not counted.",
};

const PERIOD = "Each calendar month in the date range (at most 24), then the whole range.";

function measureLabel(key: string): string {
  return gridMeasure(key)?.label ?? key;
}

function summaryLabel(key: GridSummaryColumn): string {
  return GRID_SUMMARY_COLUMNS.find((c) => c.key === key)?.label ?? key;
}

function list(labels: string[]): string {
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

function metricText(measureKey: string) {
  const metricKey = GRID_MEASURE_METRIC[measureKey];
  const def = DEFAULT_PNL_METRIC_REGISTRY.find((d) => d.metricKey === metricKey) ?? { metricKey, section: "calculated" as const, kind: "calculated" as const };
  const text = describeMetric(def);
  // An input metric's first line says how a widget folds it across projects and months; a grid cell is one project
  // in one month (or one project over the range), so that line would mislead here.
  return def.kind === "input" ? { ...text, formula: text.formula.slice(1) } : text;
}

function categoryLine(config: ResolvedGridConfig): string {
  const cfg = normaliseParetoConfig({ bands: config.paretoBands, restLabel: config.paretoRestLabel });
  const bands = cfg.bands.map((b) => `${b.label} while the projects above hold less than ${b.upToPercent}%`).join(", then ");
  return `${bands}, then ${cfg.restLabel}. The project that crosses a line stays in the band before it, so the first project is always in the first band.`;
}

/**
 * What each summary column means. Keyed by GridSummaryColumn, so a new summary
 * column without text fails to compile, and the test checks it against
 * GRID_SUMMARY_COLUMNS at run time too.
 */
const SUMMARY_TEXT: Record<GridSummaryColumn, (config: ResolvedGridConfig, s: string) => { meaning: string; formula: string[] }> = {
  total: (_config, s) => ({
    meaning: `Each measure over the whole date range.`,
    formula: [
      "The whole date range read as one span, the same figure as the Project performance Totals view.",
      `For Cost, Profit and Profit %, wages are multiplied here but not in the month cells, so the Total is not the sum of the months. The share, rank and category columns use the ${s} Total.`,
    ],
  }),
  percentOfTotal: (_config, s) => ({
    meaning: `This project's share of the ${s} Total of every row in the grid.`,
    formula: [
      `The project's ${s} Total divided by the ${s} Total of all rows (the Other row included), times 100.`,
      "Blank when the grand total is zero.",
    ],
  }),
  cumulativePercent: (_config, s) => ({
    meaning: `The running share from the project with the highest ${s} Total down to this one.`,
    formula: [
      `This project's ${s} Total plus every project ranked above it, divided by the ${s} Total of all rows, times 100.`,
      "It always counts in highest-first order, even when you sort the table another way.",
    ],
  }),
  rank: (_config, s) => ({
    meaning: `Where the project's ${s} Total places it, 1 being the highest.`,
    formula: [
      "Equal totals share a rank and the next rank is skipped (1, 2, 2, 4).",
      "The Other row takes no rank, and a project with no figure has none.",
    ],
  }),
  category: (config, s) => ({
    meaning: `Which band of the ${s} Total the project falls in: the few projects that make up most of it, or the rest.`,
    formula: [
      `Projects are taken highest ${s} Total first. ${categoryLine(config)}`,
      "The cut-offs and labels are set in the widget's settings.",
      "Blank when the grand total is zero or below.",
    ],
  }),
};

/** The grid widget's heading pop-up, from its resolved settings. */
export function describeGrid(config: PnlWidgetConfig, options: WidgetDescribeOptions = {}): MetricDefinition {
  const resolved = resolveGridConfig(config.grid);
  const measures = resolved.measures.map(measureLabel);
  const s = measureLabel(resolved.summaryMeasure);
  const summaries = resolved.summary.map(summaryLabel);
  const formula: string[] = [
    `One row per project with figures in the date range. For each month: ${list(measures)}.${summaries.length > 0 ? ` Then ${list(summaries)}.` : ""}`,
    "A month cell is that month on its own. A project with no figure that month shows a dash, not zero. A month still in progress is marked to date.",
  ];
  if (resolved.measures.some((m) => RAW_WAGES_MEASURES.has(m)) && resolved.summary.includes("total")) {
    formula.push("Month cells use wages as typed while the Total multiplies them, so the months do not add up to the Total.");
  }
  if (resolved.summary.some((c) => c !== "total")) {
    formula.push(`${list(summaries.filter((l) => l !== summaryLabel("total")))} use the ${s} Total, ranked highest first, whatever order you sort the rows in.`);
  }
  if (resolved.summary.includes("category")) formula.push(`Category: ${categoryLine(resolved)}`);
  if (resolved.topN !== null) {
    formula.push(`Shows the top ${resolved.topN} projects by ${s} Total. The rest are added together in an Other row (dollar figures only; percentage measures stay blank there).`);
  }
  if (resolved.averageRow) {
    formula.push("The Average row on top is the simple mean of every project, including those folded into Other. For a percentage that is the mean of the project percentages, not a total percentage.");
  }
  for (const rule of resolved.formatRules) {
    const def = gridMeasure(rule.measure);
    if (!def || !resolved.measures.includes(rule.measure)) continue;
    formula.push(
      def.money
        ? `${def.label} cells are coloured by bands set in the widget's settings. Safe Mode turns those colours off.`
        : `${def.label} cells are coloured: ${describeBands(rule.bands as FormatBand[], def.isPercent ? "%" : "").map((b) => `${b.label} ${lowerFirst(b.range)}`).join(", ")}.`,
    );
  }
  formula.push("Project months with income but no costs entered yet are left out, Total included, unless Show months without costs is on. The KPI cards and the Project performance Totals view count them, so their profit can be higher for the same dates.");

  const usesPayroll = resolved.measures.some((m) => metricUsesPayroll(GRID_MEASURE_METRIC[m] ?? ""));
  return {
    name: config.name,
    meaning: `Each project's ${list(measures.map(lowerFirst))} month by month${summaries.length > 0 ? `, with ${list(summaries.map(lowerFirst))}` : ""}.`,
    formula,
    source: SOURCE,
    ...widgetScope(config),
    period: PERIOD,
    lastUpdated: usesPayroll ? payrollFreshness(options.payrollAsAt) : undefined,
  };
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** The column a header trigger explains: the project column, one measure's month columns, a measure's Total, or a summary column. */
export type GridColumnTarget =
  | { kind: "name" }
  | { kind: "month"; measure: string }
  | { kind: "total"; measure: string }
  | { kind: "derived"; summary: GridSummaryColumn };

/**
 * Which visible header columns carry a trigger, and what each explains. One
 * per figure, to keep tab stops low: the project column, the FIRST visible
 * month column of each measure (it explains every month of that measure),
 * each measure's Total, and each summary column.
 */
export function gridColumnTargets(columns: readonly GridColumn[]): Map<string, GridColumnTarget> {
  const targets = new Map<string, GridColumnTarget>();
  const monthMeasures = new Set<string>();
  for (const c of columns) {
    if (c.kind === "name") targets.set(c.id, { kind: "name" });
    else if (c.kind === "month" && c.measure && !monthMeasures.has(c.measure)) {
      monthMeasures.add(c.measure);
      targets.set(c.id, { kind: "month", measure: c.measure });
    } else if (c.kind === "total" && c.measure) targets.set(c.id, { kind: "total", measure: c.measure });
    else if (c.kind === "derived") {
      const summary = c.id.replace(/^s:/, "") as GridSummaryColumn;
      if (GRID_SUMMARY_COLUMNS.some((s) => s.key === summary)) targets.set(c.id, { kind: "derived", summary });
    }
  }
  return targets;
}

/** The text on a header trigger: unique within one grid, so its accessible name is too. */
export function gridColumnName(target: GridColumnTarget): string {
  switch (target.kind) {
    case "name":
      return "Project";
    case "month":
      return `${measureLabel(target.measure)} by month`;
    case "total":
      return `Total ${measureLabel(target.measure)}`;
    case "derived":
      return summaryLabel(target.summary);
  }
}

/** One column header's pop-up. */
export function gridColumnFigure(config: PnlWidgetConfig, target: GridColumnTarget, options: WidgetDescribeOptions = {}): MetricDefinition {
  const resolved = resolveGridConfig(config.grid);
  const s = measureLabel(resolved.summaryMeasure);
  const base = { source: SOURCE, ...widgetScope(config) };
  const name = gridColumnName(target);
  const freshness = (measure: string) =>
    metricUsesPayroll(GRID_MEASURE_METRIC[measure] ?? "") ? payrollFreshness(options.payrollAsAt) : undefined;

  switch (target.kind) {
    case "name":
      return {
        ...base,
        name,
        meaning: "One row per project with figures in the date range.",
        formula: [
          resolved.averageRow
            ? "The Average row on top is the simple mean of the project rows, shown when there are two or more."
            : "The Average row is turned off for this widget.",
          resolved.topN !== null
            ? `Only the top ${resolved.topN} by ${s} Total are listed; the Other row adds the rest together.`
            : "Every project is listed.",
          "Select a project name to open its P&L tab.",
        ],
        period: PERIOD,
      };
    case "month": {
      const text = metricText(target.measure);
      const formula = ["Each column is that month on its own. A dash means no figure that month, not zero.", ...text.formula];
      if (RAW_WAGES_MEASURES.has(target.measure)) {
        formula.push("Month cells use wages as typed, like the month columns on a project's P&L tab.");
      }
      return { ...base, name, meaning: text.meaning, formula, period: "One column per calendar month in the date range.", lastUpdated: freshness(target.measure) };
    }
    case "total": {
      const text = metricText(target.measure);
      const formula = [...SUMMARY_TEXT.total(resolved, s).formula, ...text.formula];
      return { ...base, name, meaning: text.meaning, formula, period: "The whole date range.", lastUpdated: freshness(target.measure) };
    }
    case "derived": {
      const text = SUMMARY_TEXT[target.summary](resolved, s);
      return { ...base, name, meaning: text.meaning, formula: text.formula, period: "The whole date range.", lastUpdated: freshness(resolved.summaryMeasure) };
    }
  }
}
